import { CustomerService } from './services/customer'
import { AuthService } from './services/auth'

/**
 * Check if the current user is a new user (has no customers)
 * @returns {Promise<boolean>} True if user has no customers, false otherwise
 */
export const isNewUser = async () => {
    try {
        const user = AuthService.getUser()
        // Try both possible company ID locations
        const companyId = user?.CompanyId || user?.Companies?.[0]?.CompanyId

        console.log('[isNewUser] User:', user)
        console.log('[isNewUser] CompanyId (direct):', user?.CompanyId)
        console.log('[isNewUser] CompanyId (from Companies):', user?.Companies?.[0]?.CompanyId)
        console.log('[isNewUser] Final CompanyId:', companyId)

        if (!companyId) {
            console.log('[isNewUser] No companyId found, returning false')
            return false
        }

        // Get customer list with minimal data
        // Use PageSize: 10 to ensure we get all customers, not just the first one
        const result = await CustomerService.list({
            CallFrom: 1,
            PageNumber: 1,
            PageSize: 10,
        })

        console.log('[isNewUser] Customer list result:', result)
        console.log('[isNewUser] Items:', result.Items)
        console.log('[isNewUser] Items type:', typeof result.Items)
        console.log('[isNewUser] Items length:', result.Items?.length)
        console.log('[isNewUser] Items is array:', Array.isArray(result.Items))

        // Filter out the default 'Self' customer if it's the only one
        const nonSelfCustomers = result.Items?.filter(
            (customer) => customer.customer_name !== 'Self',
        )

        console.log('[isNewUser] Non-Self customers:', nonSelfCustomers)
        console.log('[isNewUser] Non-Self customers length:', nonSelfCustomers?.length)

        // If no non-Self customers exist, user is new
        const isNew = !nonSelfCustomers || nonSelfCustomers.length === 0
        console.log('[isNewUser] Is new user (after filter):', isNew)
        console.log('[isNewUser] Decision breakdown (after filter):', {
            hasItems: !!result.Items,
            isArray: Array.isArray(result.Items),
            originalLength: result.Items?.length,
            nonSelfLength: nonSelfCustomers?.length,
            isNew,
        })
        return isNew
    } catch (error) {
        console.error('[isNewUser] Error checking if user is new:', error)
        // If there's an error, assume user is not new to avoid blocking them
        return false
    }
}

/**
 * Check if the current user has templates
 * @returns {Promise<boolean>} True if user has templates, false otherwise
 */
export const userHasTemplates = async () => {
    try {
        const { TemplateService } = await import('./services/template')
        const result = await TemplateService.templateList({
            PageNumber: 1,
            PageSize: 1,
        })

        return result.Items && result.Items.length > 0
    } catch (error) {
        console.error('Error checking if user has templates:', error)
        return false
    }
}

/**
 * Check if the current user has tasks
 * @returns {Promise<boolean>} True if user has tasks, false otherwise
 */
export const userHasTasks = async () => {
    try {
        const { InspectionTaskService } = await import('./services/inspection-task')
        // Same query shape as login / GetInspectionTaskList — bare PageSize-only
        // calls return 405 on Prod (apps.lyonsinfo.com).
        const result = await InspectionTaskService.list({
            PageNumber: 1,
            PageSize: 1,
            SearchText: '',
            Status: 0,
            UserId: '',
            all: 'All',
        })
        const total = result?.TotalCount
        const itemsLength = Array.isArray(result?.Items) ? result.Items.length : 0
        return (typeof total === 'number' ? total : itemsLength) > 0
    } catch (error) {
        console.error('Error checking if user has tasks:', error)
        // Fail open for empty-account redirect: do not treat API errors as "no tasks".
        throw error
    }
}
